import React from 'react';
import { FileText, Zap } from 'lucide-react';
import Modal from './ui/Modal';

interface TemplateMatchModalProps {
  templateName: string;
  confidence: number;
  fieldCount: number;
  onAccept: () => void;
  onDecline: () => void;
}

/**
 * TemplateMatchModal - Ask user if they want to use matched template
 *
 * Shows when the system finds a matching vendor template
 * User can choose to:
 * - Accept: Use template for validation
 * - Decline: Continue with hybrid-only extraction
 */
const TemplateMatchModal: React.FC<TemplateMatchModalProps> = ({
  templateName,
  fieldCount,
  onAccept,
  onDecline
}) => {
  return (
    // Dismissing without choosing means "don't use the template" — the safe
    // default — so Escape, the backdrop and the X all map to onDecline.
    <Modal
      open
      onClose={onDecline}
      size="sm"
      title="We recognise this supplier"
      footer={
        <>
          <button
            onClick={onDecline}
            className="px-4 py-2 bg-stone-100 hover:bg-stone-200 dark:bg-coral-500/10 dark:hover:bg-coral-500/20 border border-stone-200 dark:border-coral-500/30 rounded-lg text-stone-700 dark:text-foreground-secondary transition-colors text-sm"
          >
            No thanks
          </button>
          <button
            onClick={onAccept}
            className="px-4 py-2 bg-coral-600 hover:bg-coral-700 dark:bg-coral-500 dark:hover:bg-coral-600 rounded-lg text-white font-medium transition-colors text-sm"
          >
            Yes, use it
          </button>
        </>
      }
    >
        {/* Icon */}
        <div className="flex items-center justify-center mb-4">
          <div className="p-3 bg-coral-100 dark:bg-coral-500/20 rounded-full">
            <FileText className="w-8 h-8 text-coral-600 dark:text-coral-400" />
          </div>
        </div>

        {/* Template Info */}
        <div className="mb-6 p-4 bg-coral-50 dark:bg-coral-500/10 rounded-lg border border-coral-200 dark:border-coral-500/20">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-coral-600 dark:text-foreground-muted">Template</span>
            <span className="text-sm font-medium text-stone-900 dark:text-foreground">{templateName}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-coral-600 dark:text-foreground-muted">Details we'll check</span>
            <span className="text-sm font-medium text-stone-800 dark:text-foreground-secondary">
              {fieldCount} {fieldCount === 1 ? 'detail' : 'details'}
            </span>
          </div>
        </div>

        {/* Benefits */}
        <div className="mb-6 p-3 bg-blue-50 dark:bg-blue-500/10 rounded-lg border border-blue-200 dark:border-blue-500/20">
          <div className="flex items-start gap-2">
            <Zap className="w-4 h-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-blue-800 dark:text-blue-200">
              We'll use what we learned from this supplier's invoices to read this one more accurately.
            </p>
          </div>
        </div>

        {/* Question */}
        <p className="text-center text-sm text-coral-700 dark:text-foreground-muted">
          Use it for this invoice?
        </p>
    </Modal>
  );
};

export default TemplateMatchModal;
